<template>
  <BaseModal modal-content-class="resub">
    <div class="content">
      <h1>{{ $_translations.value.modal_resubscribe_via_pin_title }}</h1>

      <BaseImage
        src="/static/images/modals/resubscribe/resubscribe_pin_icon.svg"
        alt="mobile image"
        image-class="modal_simple_img"
      />

      <p>{{ $_translations.value.modal_resubscribe_via_pin_description }}</p>

      <BaseDigitFormInput
        :model-value="formValues.pin.state"
        :label="$_translations.value.modal_resubscribe_via_pin_input_label_validation_code"
        :error="formValues.pin.validation.error"
        :error-message="$_translations.value[formValues.pin.validation.message]"
        :max-length="appSettingsStore.appProperties.maxLengthPinDigits"
        input-id="pin_sms"
        @update:modelValue="formValues.pin.setState"
      />

      <BaseButton
        container-class="resub_btn"
        :disabled="submitting"
        @click:button="handleProceed"
      >
        {{ $_translations.value.modal_resubscribe_via_pin_button_proceed }}
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script setup>
import BaseModal from '@/components/modal/BaseModal.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseDigitFormInput from '@/components/formInput/BaseDigitFormInput.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useIntegrationStore from '@/store/modules/integration.js';
import useFormHook from '@/hooks/useForm.js';
import useUILockHook from '@/hooks/useUILock.js';
import { apiGetStatusVerificationPin, apiVerifyReoptinPin } from '@/services/general.js';
import { apiResponseTypeEnum } from '@/constants/api.js';

const emits = defineEmits(['resubscribe:viaPin']);

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();
const integrationStore = useIntegrationStore();

const { formValues, isFormInvalid, resetFormErrors } = useFormHook({ pin: '' });
const [submitting, setSubmittingState] = useUILockHook();

const validateForm = () => {
  if (!formValues.pin.state) {
    formValues.pin.setValidation({ error: true, message: 'common_error_input_field_required' });
  }

  return isFormInvalid();
};

const handleProceed = async () => {
  try {
    setSubmittingState(true);

    resetFormErrors();

    const invalid = validateForm();

    if (invalid) { return; }

    const { responseEnum } = await apiVerifyReoptinPin({ payload: { pin: formValues.pin.state } });

    if (responseEnum === apiResponseTypeEnum.OPERATION_SUCCESS) {
      if (integrationStore.hasActiveIntegrationAsync) {
        await apiGetStatusVerificationPin({ queryParams: { msisdn: authStore.user.msisdn } });
      }

      await authStore.dispatchRetrieveAuthUser();

      emits('resubscribe:viaPin', { status: 'success' });
    } else if ([apiResponseTypeEnum.WRONG_PIN, apiResponseTypeEnum.PIN_EXPIRED].includes(responseEnum)) {
      formValues.pin.setValidation({ error: true, message: 'modal_resubscribe_via_pin_error_validation_code_invalid' });
    } else {
      formValues.pin.setValidation({ error: true, message: 'common_error_server_unavailable' });
    }
  } catch {
    emits('resubscribe:viaPin', { status: 'error' });
  } finally {
    setSubmittingState(false);
  }
};
</script>
